import _ from "lodash";
import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { PermissionService } from "src/services/permissionService/permissionService";
import { useDispatch, useSelector } from "src/store";

import { ConfirmDialog } from "@dashboard/devices/components/shared/index";
import BuildsTable from "@dashboard/products/components/Builds/BuildsTable";
import { useStyles } from "@dashboard/products/ProductPage.style";
import { deleteProduct, fetchProductBuilds } from "@dashboard/products/store/index";
import { selectProducts, selectProductStatus } from "@dashboard/products/store/selectors/products";
import { Product } from "@dashboard/products/types/index";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import PageHeader from "@dashboard/shared/components/PageHeader";
import { useCurrentWorkspace, useIsWorkspaceFeatureLimitReached } from "@dashboard/workspaces/api/helpers";
import { DialogContentText } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { ExpiredWrapper } from "@shared/ExpiredWrapper";
import { SelectField } from "@shared/form/SelectField/SelectField";
import { SingleSelectFieldProps } from "@shared/form/SelectField/SelectField.types";
import { LoadingBox } from "@shared/Loading";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { SidePanel } from "../shared/components/SidePanel/SidePanel";

import { ProductBuilder } from "./components/ProductBuilder/ProductBuilder";
import { useChangeProductFromUrl, useCurrentProduct } from "./ProductPage.utils";

export const ProductManagerPage = (props: { products: Product[] }) => {
    const [currentProductId, setCurrentProductId] = useCurrentProduct(props.products);
    const [isDeleteProductDialogOpen, setIsDeleteProductDialogOpen] = React.useState(false);

    const { classes } = useStyles();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { t } = useTranslation(["pages", "common", "modals"]);

    const { data: workspace } = useCurrentWorkspace();
    const isOverlimit = useIsWorkspaceFeatureLimitReached("ProductLimit");

    const changeProductFromUrl = useChangeProductFromUrl({ setCurrentProductId });

    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");

    const currentProduct = props.products.find((item) => item.id === currentProductId);
    const isCreationProcess = !currentProduct?.id;
    const productOptions = props.products.map((product) => ({
        label: `${product.customBuildConfig?.productName || ""} - Android ${product.androidVersion}`,
        value: product.id,
    }));

    const updateProductUrl = (product: Product | undefined) => {
        if (product) {
            void navigate(`/dashboard/products/${product.codename}/${product.androidVersion}`);
        }
    };

    const onCreateProduct = () => {
        setCurrentProductId(null);
        void navigate("/dashboard/products");
    };

    const switchToProduct = async (product: Product) => {
        try {
            await dispatch(fetchProductBuilds(product.id));
            setCurrentProductId(product.id);
            updateProductUrl(product);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : t("pages:productPage.toast.failedToFetchBuilds");
            showErrorToast(errorMessage);
        }
    };

    const handleProductChange: SingleSelectFieldProps["onChange"] = async (_event, selectedOption) => {
        if (!selectedOption || typeof selectedOption.value !== "number") {
            return;
        }

        const selectedProduct = props.products.find((product) => product.id === selectedOption.value);
        if (!selectedProduct) {
            return;
        }

        await switchToProduct(selectedProduct);
    };

    const onCancelCreation = async () => {
        const firstProduct = props.products[0];
        if (!firstProduct) {
            return;
        }
        await switchToProduct(firstProduct);
    };

    const getCreateButtonTooltip = () => {
        if (isOverlimit) {
            return t("pages:productPage.tooltip.maxProductLimitReached");
        }
        if (cannotManageProducts) {
            return t("pages:productPage.tooltip.noPermissionToCreateProduct");
        }
        return "";
    };

    const confirmDeleteProductDialog = async () => {
        if (currentProductId) {
            try {
                await dispatch(deleteProduct(currentProductId)).unwrap();
                showSuccessToast(t("pages:productPage.toast.successfullyDeletedProduct"));
                const filterProducts = props.products.filter((item) => item.id !== currentProductId);
                setCurrentProductId(filterProducts[0]?.id);
            } catch (error) {
                const errorMessage = error instanceof Error ? error.message : t("pages:productPage.toast.failedToDeleteProduct");
                showErrorToast(errorMessage);
            } finally {
                setIsDeleteProductDialogOpen(false);
            }
        }
    };

    React.useEffect(() => {
        void changeProductFromUrl();
    }, []);

    return (
        <React.Fragment>
            <PageContainer>
                <PageHeader
                    title={t("pages:productPage.title")}
                    iconName="fa-solid fa-microchip"
                    description={t("pages:productPage.description")}
                />

                <ExpiredWrapper>
                    <div className={classes.productContainer}>
                        <SelectField
                            label={
                                isCreationProcess
                                    ? t("pages:productPage.input.createNewProduct.caption")
                                    : t("common:label.product")
                            }
                            value={productOptions.find((option) => option.value === currentProductId)}
                            onChange={handleProductChange}
                            options={productOptions}
                            disabled={!props.products.length}
                            fullWidth
                            searchable
                        />

                        <div className={classes.buttonsContainer}>
                            <DangerOutlinedButton
                                onClick={() => setIsDeleteProductDialogOpen(true)}
                                className={classes.deleteButton}
                                disabled={!props.products.length || cannotManageProducts || isCreationProcess}
                                tooltipProps={{
                                    title: t("pages:productPage.tooltip.noPermissionToDeleteProduct"),
                                    hide: !cannotManageProducts,
                                }}
                            >
                                {t("common:button.delete")}
                            </DangerOutlinedButton>
                            <PrimaryOutlinedButton
                                onClick={onCreateProduct}
                                disabled={isOverlimit || cannotManageProducts || isCreationProcess}
                                tooltipProps={{
                                    title: getCreateButtonTooltip(),
                                }}
                            >
                                {t("common:button.create")}
                            </PrimaryOutlinedButton>
                        </div>
                    </div>
                    {isCreationProcess && isOverlimit ? (
                        <CustomAlert severity="warning" className={classes.alertContainer}>
                            {getCreateButtonTooltip()}
                        </CustomAlert>
                    ) : null}
                    <React.Fragment>
                        <ProductBuilder
                            product={currentProduct}
                            setCurrentProductId={setCurrentProductId}
                            onCancelCreation={onCancelCreation}
                        />
                        {currentProduct ? <BuildsTable product={currentProduct} /> : null}
                    </React.Fragment>
                </ExpiredWrapper>
            </PageContainer>
            <ConfirmDialog
                title={t("modals:deleteProduct.title")}
                open={isDeleteProductDialogOpen}
                onConfirm={confirmDeleteProductDialog}
                onClose={() => setIsDeleteProductDialogOpen(false)}
                dangerButton
                primaryActionText={t("common:button.delete")}
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="deleteProduct.description"
                        values={{ productName: currentProduct?.customBuildConfig?.productName || "" }}
                        components={{ bold: <span className={classes.boldText} /> }}
                    />
                </DialogContentText>
            </ConfirmDialog>
            <SidePanel />
        </React.Fragment>
    );
};

const ProductPage = () => {
    const isLoading = useSelector(selectProductStatus);
    const products = useSelector(selectProducts);
    const sortedProducts = _.sortBy([...products], (item) => [item.customBuildConfig?.productName?.toLowerCase()]);

    if (!isLoading || isLoading === "pending") {
        return <LoadingBox />;
    }

    return <ProductManagerPage products={sortedProducts} />;
};

export default withMetaDescription(ProductPage, {
    title: i18next.t("pages:productPage.metaTitle"),
    description: i18next.t("pages:productPage.metaDescription"),
});
